import { useQuery } from '@tanstack/react-query'

import { API } from '@/api'
import { Instance } from '@/models/instance'
import { TIME_UNITS_IN_MILLS } from '@/utils/time'

interface UseInstanceListQueryParams {
  page: number
  pageSize: number
  statusCodes?: number[]
  regions: number[]
  regionFilterCriteria: number
  enabled?: boolean
}

export function useInstanceListQuery({
  page,
  pageSize,
  statusCodes,
  regions,
  regionFilterCriteria,
  enabled = true,
}: UseInstanceListQueryParams) {
  const queryResult = useQuery({
    // queryKey: All parameters that affect the data
    // React Query automatically compares these and cancels old requests when they change
    queryKey: [
      'instanceList',
      page,
      statusCodes?.join(','),
      regions.join(','),
      regionFilterCriteria,
    ],

    // queryFn: The actual request function
    // { signal } is automatically injected by React Query for request cancellation
    queryFn: async ({ signal }) => {
      let apiRegionFilterCriteria = 0
      const REGION_DATA_DRIVE = -2
      const REGION_MESH_CLUSTERING = -3

      if (regionFilterCriteria === 0 || regionFilterCriteria === -1) {
        apiRegionFilterCriteria = 0
      } else if (regionFilterCriteria === REGION_DATA_DRIVE) {
        apiRegionFilterCriteria = 1
      } else if (regionFilterCriteria === REGION_MESH_CLUSTERING) {
        apiRegionFilterCriteria = 2
      }

      // Pass signal to API call for automatic cancellation
      const res = await API.getNodeList(
        {
          page,
          per_page: pageSize,
          statuses: statusCodes?.join(','),
          regions: regions,
          region_filter_criteria: apiRegionFilterCriteria,
        },
        { signal },
      )

      if (res.code !== 0) {
        throw new Error(`Failed to fetch instances: ${res.msg}`)
      }

      return {
        instances: res.userNodes.map((data) => new Instance(data)),
        pagination: res.pagination,
      }
    },

    // Dynamic refetch interval based on instance status
    // Fast polling (1s) when instances are in progress, otherwise 5s
    refetchInterval: (query) => {
      const instances = query.state.data?.instances ?? []
      const hasInProgress = instances.some((item) => item.inProgress)
      return hasInProgress ? TIME_UNITS_IN_MILLS.SECOND : 5 * TIME_UNITS_IN_MILLS.SECOND
    },

    // Data is always considered stale (immediate refetch on focus/mount)
    staleTime: 0,

    enabled,
  })

  return {
    instances: queryResult.data?.instances ?? [],
    total: queryResult.data?.pagination.total ?? 0,
    currentPage: queryResult.data?.pagination.page ?? page,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  }
}
